import { get } from 'lodash';
import { formatColor } from '@meitu/saas-colors';
import { hexToHsva, hsvaToHexa } from '@meitu/saas-colors/dist/esm/utils/color-convert';
import { trackConfig, type IFramework } from '@/framework';
import {
    GradientColorResult,
    LayerType,
    type EditingRange,
    type ILayerProps,
    type ITextLayerProps,
    type ITexture,
} from '@/version-types';
import { pushHistory, RichTextProcessor, updateGroupLayer, updateLayer, validateInputLayer } from '../utils';
import { type IShapeText } from './shape-text';

export interface IFillColor {
    /**
     * 设置文字颜色
     * @param layerID - 图层 ID
     * @param color - 颜色
     * @param options - 配置项
     * @returns
     */
    __setFillColor(
        layerID: string,
        color: string,
        options?: {
            range?: EditingRange | null;
            originLayer?: ILayerProps;
        },
    ): Promise<{ layer: ILayerProps; range: EditingRange | null } | void>;

    /**
     * 设置文字透明度
     * @param layerID - 图层 ID
     * @param opacity - 透明度
     * @param options - 配置项
     * @returns
     */
    setFillOpacity(
        layerID: string,
        opacity: number,
        options?: {
            originLayer?: ILayerProps;
        },
    ): Promise<ILayerProps | void>;

    /**
     * 设置纹理
     * @param layerID - 图层 ID
     * @param texture - 纹理配置
     * @returns
     */
    setTexture(layerID: string, texture: ITexture): Promise<ILayerProps | void>;
}

export class FillColor implements IFillColor {
    constructor(
        private framework: IFramework,
        private shapeText: IShapeText,
    ) {}

    /**
     * 设置文字渐变色
     * ATTENTION: 多段编辑情况下暂不支持渐变色设置，所以这里不需要 editingRange
     * @param layer - 图层数据
     * @param color - 颜色
     * @returns
     */
    private setWithGradientColor(layer: ILayerProps, color: string) {
        const prevLayerData = layer.data as ITextLayerProps;
        const textGradient = formatColor(color) as GradientColorResult;

        const nextLayerData: ITextLayerProps = {
            ...prevLayerData,
            textGradient,
            texture: undefined,
            lineThrough: false,
            underline: false,
        };

        const nextLayer: ILayerProps = {
            ...layer,
            data: nextLayerData,
        };

        return nextLayer;
    }

    /**
     * 设置文字颜色
     * ATTENTION: 多段编辑情况下，如果当前图层有使用渐变色，需要取消渐变色
     * @param layer - 图层数据
     * @param color - 颜色
     * @param range - 范围
     * @returns
     */
    private setWithNormalColor(layer: ILayerProps, color: string, range?: EditingRange | null) {
        const prevLayerData = layer.data as ITextLayerProps;
        let nextLayerData: ITextLayerProps = { ...prevLayerData, textGradient: null, texture: undefined };

        if (!range || range.isCollapsed) {
            nextLayerData = { ...nextLayerData, color };

            if (nextLayerData.textContents && nextLayerData.textContents.length > 0) {
                const nextTextContents = nextLayerData.textContents.map((item) => ({
                    ...item,
                    color,
                }));
                nextLayerData = { ...nextLayerData, textContents: nextTextContents };
            }

            const nextLayer: ILayerProps = { ...layer, data: nextLayerData };

            // 若当前图层是花字场景，需要将修改的颜色同步到该字段中
            if (prevLayerData.advancedTextMaterial) {
                const extraInfo =
                    layer.extraInfo && Object.prototype.toString.call(layer.extraInfo) === '[object Object]'
                        ? { ...layer.extraInfo }
                        : {};
                extraInfo.overrideColorInArtTextCase = color;
                nextLayer.extraInfo = extraInfo;
            }

            return { layer: nextLayer, range: null };
        }

        const { textContents, range: nextRange } = RichTextProcessor.process(prevLayerData, range, {
            key: 'color',
            value: color,
        });
        nextLayerData = { ...nextLayerData, textContents };
        const nextLayer: ILayerProps = { ...layer, data: nextLayerData };

        return { layer: nextLayer, range: nextRange };
    }

    private async setFillColor(
        layer: ILayerProps,
        color: string,
        options: { range?: EditingRange | null; activate?: boolean; originLayer?: ILayerProps } = {},
    ) {
        const { activate = true, originLayer } = options;
        const range = options.range || null;

        let nextLayer: ILayerProps = { ...layer };
        let nextRange: EditingRange | null = range;

        if (color.startsWith('linear-gradient')) {
            nextLayer = this.setWithGradientColor(layer, color);
        } else {
            const result = this.setWithNormalColor(layer, color, range);
            nextLayer = result.layer;
            nextRange = result.range;
        }

        const shapeExtension = get(layer, 'data.shapeExtension', null);
        if (shapeExtension && originLayer) {
            const layer = await this.shapeText.refreshLayerScreenshotURL(nextLayer);
            if (layer) nextLayer = layer;
            updateLayer(this.framework, nextLayer, { activate });
        }

        updateLayer(this.framework, nextLayer, { activate });
        if (originLayer) pushHistory(this.framework, originLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER].color);

        return { layer: nextLayer, range: nextRange };
    }

    async __setFillColor(
        layerID: string,
        color: string,
        options: { range?: EditingRange | null; originLayer?: ILayerProps },
    ) {
        const { range, originLayer } = options;

        const layer = this.framework.getLayer(layerID, true);
        if (!validateInputLayer(layer)) return;

        if (layer.layerType === LayerType.GROUP_LAYER) {
            const children = layer.children;
            if (children.length === 0) return;

            const results = await Promise.all(
                children.map((child) => this.setFillColor(child, color, { activate: false })),
            );
            const layers = results.map((result) => result.layer);

            const nextLayer = { ...layer, children: layers };

            this.framework.stage.addOrUpdate(nextLayer);
            if (originLayer)
                pushHistory(this.framework, originLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER].color);

            return { layer: nextLayer, range: null };
        }

        const result = await this.setFillColor(layer, color, { range, activate: true, originLayer });

        return result;
    }

    private async __setFillOpacity(
        layer: ILayerProps,
        opacity: number,
        options: { activate?: boolean; originLayer?: ILayerProps } = {},
    ) {
        const { activate = true, originLayer } = options;

        const layerData = layer.data as ITextLayerProps;
        const { color } = layerData;

        const nextColor = hsvaToHexa({ ...hexToHsva(color), a: opacity / 100 });

        let nextLayerData: ITextLayerProps = { ...layerData, color: nextColor };

        const prevTextContents = layerData.textContents;
        if (prevTextContents && prevTextContents.length > 0) {
            const nextTextContents = prevTextContents.map((item) => ({
                ...item,
                color: nextColor,
            }));
            nextLayerData = { ...nextLayerData, textContents: nextTextContents };
        }

        let nextLayer: ILayerProps = { ...layer, data: nextLayerData };

        const { shapeExtension } = layerData;
        if (shapeExtension && originLayer) {
            const layer = await this.shapeText.refreshLayerScreenshotURL(nextLayer);
            if (layer) nextLayer = layer;

            updateLayer(this.framework, nextLayer, { activate });
        }

        updateLayer(this.framework, nextLayer, { activate });
        if (originLayer) pushHistory(this.framework, originLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER].opacity);

        return nextLayer;
    }

    async setFillOpacity(layerID: string, opacity: number, options: { originLayer?: ILayerProps } = {}) {
        const { originLayer } = options;

        const layer = this.framework.getLayer(layerID, true);
        if (!validateInputLayer(layer)) return;

        if (layer.layerType === LayerType.GROUP_LAYER) {
            const children = layer.children;
            if (children.length === 0) return;

            const layers = await Promise.all(
                children.map((child) => this.__setFillOpacity(child, opacity, { activate: false })),
            );

            const nextLayer = { ...layer, children: layers };

            this.framework.stage.addOrUpdate(nextLayer);
            if (originLayer)
                pushHistory(this.framework, originLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER].opacity);

            return nextLayer;
        }

        const nextLayer = await this.__setFillOpacity(layer, opacity, { activate: true, originLayer });

        return nextLayer;
    }

    private async __setTexture(
        prevLayer: ILayerProps,
        texture: ITexture,
        options: { activate?: boolean; writeHistory?: boolean } = {},
    ) {
        const { activate = true, writeHistory = false } = options;

        const prevLayerData = prevLayer.data as ITextLayerProps;

        // ATTENTION：使用纹理需要禁用下划线和删除线
        const nextLayerData: ITextLayerProps = {
            ...prevLayerData,
            texture,
            textGradient: null,
            lineThrough: false,
            underline: false,
        };
        let nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        const { shapeExtension } = prevLayerData;
        if (shapeExtension) {
            const layer = await this.shapeText.refreshLayerScreenshotURL(nextLayer);
            if (layer) nextLayer = layer;
        }

        updateLayer(this.framework, nextLayer, { activate });
        if (writeHistory) pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER].texture);

        return nextLayer;
    }

    async setTexture(layerID: string, texture: ITexture) {
        const prevLayer = this.framework.getLayer(layerID, true);
        if (!validateInputLayer(prevLayer)) return;

        if (prevLayer.layerType === LayerType.GROUP_LAYER) {
            const children = prevLayer.children;
            if (children.length === 0) return;

            const results = await Promise.all(
                children.map((child) => this.__setTexture(child, texture, { activate: false })),
            );
            const layers = results.filter(Boolean) as ILayerProps[];

            const nextLayer = updateGroupLayer(this.framework, prevLayer, layers, {
                funcID: trackConfig[LayerType.TEXT_LAYER].texture,
                writeHistory: true,
                update: true,
            });

            return nextLayer;
        }

        return this.__setTexture(prevLayer, texture, { activate: true, writeHistory: true });
    }
}
